import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building2, ShieldCheck } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import FoxLogo from "@/components/FoxLogo";

const SIDE_IMG = "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=1400&q=80";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t("auth.welcome_back"));
      // Admin → pannello operativo; altri → dashboard host
      nav(user?.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.login_error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src={SIDE_IMG} alt="reception" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-slate-50">
          <Link to="/" className="font-heading font-semibold text-2xl inline-flex items-center gap-2"><FoxLogo size={32} />Xenia<span className="text-sky-300">.</span></Link>
          <div>
            <p className="text-xs tracked text-sky-300 mb-4">{t("auth.access_label_login")}</p>
            <h2 className="font-heading font-semibold text-4xl tracking-tight max-w-md">{t("auth.side_quote_login")}</h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6"><LanguageSelector /></div>
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="md:hidden flex items-center gap-2 mb-6">
            <FoxLogo size={28} />
            <span className="font-heading font-semibold text-2xl">Xenia</span>
          </div>
          <h1 className="font-heading font-semibold text-4xl tracking-tight mb-2">{t("auth.login_title")}</h1>
          <p className="text-slate-600 text-sm mb-6">{t("auth.login_sub")}</p>

          {/* Google Login */}
          <button type="button" onClick={googleLogin} data-testid="google-login"
            className="w-full flex items-center justify-center gap-3 border border-slate-300 bg-white px-4 py-3 text-sm hover:bg-slate-50 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {t("auth.google_button")}
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-500">
            <span className="flex-1 h-px bg-stone-200"></span>
            {t("auth.or")}
            <span className="flex-1 h-px bg-stone-200"></span>
          </div>

          <label className="block text-xs tracked text-slate-500 mb-2">{t("common.email").toUpperCase()}</label>
          <input data-testid="login-email" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-slate-300 px-4 py-3 mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 bg-white text-sm" />

          <label className="block text-xs tracked text-slate-500 mb-2">{t("common.password").toUpperCase()}</label>
          <input data-testid="login-password" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-slate-300 px-4 py-3 mb-6 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 bg-white text-sm" />

          <button data-testid="login-submit" disabled={loading}
            className="w-full bg-gradient-to-br from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg hover:from-blue-500 hover:to-blue-600 shadow-[0_8px_20px_-6px_rgba(37,99,235,0.4)] font-semibold transition-all text-sm disabled:opacity-60">
            {loading ? "..." : t("auth.login_title")}
          </button>

          <div className="mt-4 inline-flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
            {t("auth.security_cue", "Accesso protetto · dati cifrati end-to-end")}
          </div>

          <p className="mt-6 text-sm text-slate-600">
            {t("auth.no_account")}{" "}
            <Link to="/signup" data-testid="goto-signup" className="text-blue-600 hover:underline underline-offset-4">{t("auth.register")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
